import type { DataSession } from "../data/core/session";
import {
  fetchCDNKey,
  getCDNKeyFromCache,
} from "@hooks/trpc/useCDNKey/queryFunctions";
import { captureException } from "@utils/captureException";
import { router } from "expo-router";
import { TABS, Tab as LTabs } from "@utils/navigation/helpers";
import dayjs from "dayjs";
import { backOff } from "exponential-backoff";
import { getActiveTab } from "@hooks/useActiveTab";

/**
 * Bunny playback URLs live in two namespaces at the CDN zone root, each
 * unlocked by its own wide directory token (see `cdn_keys` / `sign-cdn-url`):
 *   - `/audio/…`  → legacy catalog        → `bunnyCDNPath`
 *   - `/songs/…`  → on-demand publish      → `bunnyCDNPathSongs`
 *
 * Public previews (`/songs/<id>/preview/…`) are served UNSIGNED via a Bunny
 * disable-token rule, so we deliberately never attach a token to them —
 * signing them would start requiring a token client-side. A preview
 * environment's own media (`/preview/pr-<n>/songs/…`) is not a public preview
 * and is signed like any song. `/audio/` wins when (pathologically) both
 * appear, preserving legacy behavior exactly.
 */
type CdnNamespace = "audio" | "songs";

const PUBLIC_SONG_PREVIEW = /\/songs\/[^/]+\/preview\//;

function cdnNamespaceForUrl(url: string): CdnNamespace | null {
  if (url.includes("/audio/")) return "audio";
  if (url.includes("/songs/") && !PUBLIC_SONG_PREVIEW.test(url)) return "songs";
  return null;
}

function cdnTokenForNamespace(
  key: {
    bunnyCDNPath: string | null;
    bunnyCDNPathSongs?: string | null;
  },
  namespace: CdnNamespace,
): string | null {
  return namespace === "audio"
    ? key.bunnyCDNPath
    : key.bunnyCDNPathSongs ?? null;
}

// Rate-limits the missing-namespace-token upgrade below: signing a whole
// playlist can degrade dozens of URLs in one frame, and they must not each
// pay a network wait. One attempt per minute is plenty — a successful fetch
// writes through the shared query cache, so the first upgrade fixes every
// subsequent sign call synchronously.
const MISSING_NAMESPACE_TOKEN_UPGRADE_COOLDOWN_MS = 60_000;
// Hard cap on how long one sign call may wait for the key upgrade — past
// this, return the unsigned URL (the old degrade) rather than stall playback
// on a slow network. The raced-out fetch still settles into the query cache,
// so a later attempt signs synchronously.
const MISSING_NAMESPACE_TOKEN_UPGRADE_WAIT_MS = 2_000;
const lastMissingNamespaceTokenUpgrade = new WeakMap<DataSession, number>();

/**
 * Degrade-rescue for `signUrl`: the cached key is valid but carries no token
 * for THIS URL's namespace (a key minted before the two-token rotation).
 * Await ONE bounded key refetch and sign with the fresh key — turning what
 * used to be a guaranteed 403 on the first post-rotation play into a short
 * wait. Returns null when upgrading isn't possible (cooldown, signed out,
 * fetch failed/timed out, fresh key still tokenless); callers then return
 * the unsigned URL — exactly the pre-upgrade degrade, never worse.
 */
async function signViaUpgradedKey(
  data: DataSession,
  url: string,
  namespace: CdnNamespace,
): Promise<string | null> {
  if (!data.auth.currentUser()) return null;
  const now = Date.now();
  if (
    now - (lastMissingNamespaceTokenUpgrade.get(data) ?? 0) <
    MISSING_NAMESPACE_TOKEN_UPGRADE_COOLDOWN_MS
  ) {
    return null;
  }
  lastMissingNamespaceTokenUpgrade.set(data, now);

  let key: Awaited<ReturnType<typeof fetchCDNKey>>;
  // The timer must be cleared when the fetch wins the race — a fired-later
  // reject with no listener would surface as an unhandled promise rejection.
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    key = await Promise.race([
      fetchCDNKey(data),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error("cdn-key upgrade timed out")),
          MISSING_NAMESPACE_TOKEN_UPGRADE_WAIT_MS,
        );
      }),
    ]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
  if (!key || dayjs(key.expiry).isBefore(dayjs())) return null;
  const token = cdnTokenForNamespace(key, namespace);
  if (!token) return null;

  const urlComponent = new URL(url);
  urlComponent.pathname = `${token}${urlComponent.pathname}`;
  return urlComponent.toString();
}

export function signUrlSyncIfPossible(
  data: DataSession,
  url: string | null | undefined,
): string | null {
  if (!url) return "";

  // Only sign urls if not a local file
  if (url.startsWith("file:")) return url;
  // Only sign urls if not already signed
  if (url.includes("bcdn_token=")) return url;

  const namespace = cdnNamespaceForUrl(url);
  if (!namespace) {
    return url;
  }

  const key = getCDNKeyFromCache(data);
  if (!key) return null;
  if (dayjs(key.expiry).isBefore(dayjs())) return null;

  // The token for this namespace may be absent (e.g. a `/songs/` URL before
  // the first two-token rotation lands): degrade to the unsigned URL rather
  // than emitting a `null…` prefix or forcing a pointless key refetch.
  const token = cdnTokenForNamespace(key, namespace);
  if (!token) return url;

  const urlComponent = new URL(url);
  const pathname = urlComponent.pathname;
  urlComponent.pathname = `${token}${pathname}`;
  return urlComponent.toString();
}

export async function signUrl(
  data: DataSession,
  url: string | null | undefined,
  options?: { forceRefreshCDNKey?: boolean },
): Promise<string> {
  if (!url) return "";

  // Only sign urls if not a local file
  if (url.startsWith("file:")) return url;
  // Only sign urls if not already signed
  if (url.includes("bcdn_token=")) return url;

  const namespace = cdnNamespaceForUrl(url);
  if (!namespace) {
    return url;
  }

  const sync = options?.forceRefreshCDNKey
    ? null
    : signUrlSyncIfPossible(data, url);
  if (sync !== null) {
    // For a namespaced URL, `sync === url` means exactly one thing: the cached
    // key is present and unexpired but carries no token for THIS namespace —
    // signUrlSyncIfPossible's degrade path (a key cached before the two-token
    // rotation, seen live when `/songs/` URLs start appearing mid-session).
    // Await one bounded key refetch and sign with it, so the first
    // post-rotation play pays ~a second instead of 403ing; every
    // cannot-upgrade case falls through to the unsigned URL — the old
    // degrade, never worse.
    if (sync === url) {
      const upgraded = await signViaUpgradedKey(data, url, namespace);
      if (upgraded !== null) return upgraded;
    }
    return sync;
  }

  // Get cached key first
  let key = getCDNKeyFromCache(data);

  // Check if key exists and is not expired
  const expired =
    options?.forceRefreshCDNKey || !key || dayjs(key.expiry).isBefore(dayjs());

  if (expired) {
    // Check if user is authenticated before attempting to fetch
    if (!data.auth.currentUser()) {
      return url;
    }

    // Key is missing or expired, fetch a new one with retry
    try {
      key = await backOff(
        async () => {
          const cdnKey = await fetchCDNKey(data);
          if (!cdnKey || dayjs(cdnKey.expiry).isBefore(dayjs())) {
            throw new Error("Invalid or expired CDN key");
          }
          return cdnKey;
        },
        {
          numOfAttempts: 3,
          startingDelay: 150,
          timeMultiple: 1,
        },
      );
    } catch (error) {
      captureException(error, {
        channel: "error",
        area: "media",
        operation: "fetch-cdn-key",
      });
      // Fallback to unsigned URL if fetch fails
      return url;
    }
  }

  if (!key) {
    captureException("No CDN key found after fetch", {
      channel: "error",
      area: "media",
      operation: "missing-cdn-key-after-fetch",
    });
    return url;
  }
  // Same degrade-to-unsigned guard as the sync path: if this namespace's
  // token is missing even after the fetch, don't emit a `null…` prefix.
  const token = cdnTokenForNamespace(key, namespace);
  if (!token) return url;

  const urlComponent = new URL(url);
  const pathname = urlComponent.pathname;
  urlComponent.pathname = `${token}${pathname}`;
  const signedUrl = urlComponent.toString();

  return signedUrl;
}

type ImageSize = "small" | "medium" | "large" | "xLarge";

export const getImageUrlWithSize = (
  url: string | undefined | null,
  imageSize: ImageSize,
) => {
  if (!url) return;
  if (url.includes("file:")) {
    // dont change local files
    return url;
  }

  if (!url.includes("lissen.live")) {
    return url;
  }

  let size = "";

  switch (imageSize) {
    case "small":
      size = "_size=128";
      break;
    case "medium":
      size = "_size=256";
      break;
    case "large":
      size = "_size=512";
      break;
    case "xLarge":
      size = "_size=1024";
      break;
  }

  if (url.includes("_size=")) {
    return url.split("_size=").at(0) + size;
  }

  return url + size;
};

export const secondsToTime = (secs: number, extraSecs: number = 0) => {
  const totalSeconds = secs + extraSecs;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    const minString = minutes.toString().padStart(2, "0");
    const secString = seconds.toString().padStart(2, "0");
    return `${hours}:${minString}:${secString}`;
  }

  let min: string | number = parseInt((totalSeconds / 60).toString(), 10);
  if (Math.abs(min) < 10) {
    min = `0${min}`;
  }
  const sec = totalSeconds % 60;
  let secString;
  if (sec === 60) {
    secString = "00";
  } else if (sec > 60) {
    const secRemain = sec % 60;
    secString = secRemain < 10 ? `0${secRemain}` : `${secRemain}`;
  } else if (sec < 10) {
    secString = `0${sec}`;
  } else {
    secString = `${sec}`;
  }

  return `${min}:${secString}`;
};

export const getLibraryItemMainFields = (
  doc:
    | {
        image: string | null;
        title: string;
        type: "album";
      }
    | {
        images: string[] | null;
        title: string;
        type: "playlist";
        user: {
          id: string;
        };
      }
    | {
        image: string | null;
        name: string;
        type: "artist";
      },
  user?: { uid: string },
) => {
  const { type } = doc;
  const title = type === "artist" ? doc.name : doc.title;
  const own = type === "playlist" ? doc.user?.id === user?.uid : false;

  //TODO: re-add
  //   const { isCompleted: isDownloaded } = useDownloadedCollectionStatus(item.id);
  const isDownloaded = false;

  const getImages = () => {
    switch (type) {
      case "artist": {
        return { images: [doc.image] };
      }
      case "album":
        return { images: [doc.image] };
      case "playlist":
        return { images: doc.images };
      default:
        return { images: [""] };
    }
  };

  const { images } = getImages();

  return {
    own,
    title,
    isDownloaded,
    images,
  };
};

export { TABS, LTabs };

export const currencyFormat = (value?: number, roundDown?: boolean) => {
  if (!value) return "£0.00";
  let text;

  if (!roundDown) {
    text = `£${Number(value)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  } else {
    text = `£${Number(value)
      .toFixed(0)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  }
  return text;
};

interface handleArtistNavigationParams {
  isPlaylist: boolean;
  numberOfArtists: number;
  artistId?: string;
}
interface handleArtistNavigationProps {
  params: handleArtistNavigationParams;
}
export function handleArtistNavigation({
  params,
}: handleArtistNavigationProps) {
  if (params.isPlaylist || params.numberOfArtists > 2 || !params.artistId) {
    return;
  }
  return router.navigate(
    `/(app)/(tabs)/${getActiveTab()}/artist/${params.artistId}`,
  );
}
